import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ListenersPanel from './components/ListenersPanel';
import ConversationPanel from './components/ConversationPanel';
import ToolsPanel from './components/ToolsPanel';
import { LoadingSkeleton } from '@/components/base/LoadingSkeleton';
import { ErrorState } from '@/components/base/ErrorState';

type MobilePanel = 'oyentes' | 'conversacion' | 'herramientas';

export default function AdminStudioPage() {
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>('conversacion');
  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadData = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.05;
      setPageState(shouldError ? 'error' : 'ready');
    }, 500);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-secondary-500 flex items-center justify-center">
              <i className="ri-broadcast-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Studio</h1>
              <p className="text-foreground-400 text-[9px]">
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block"></span>
                  En vivo · Tardes de Pop
                </span>
              </p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
          </Link>
        </div>
        {/* Mobile panel selector */}
        <div className="flex items-center gap-1 px-3 pb-2">
          {[
            { id: 'oyentes' as MobilePanel, label: 'Oyentes', icon: 'ri-user-voice-line', count: '15' },
            { id: 'conversacion' as MobilePanel, label: 'Chat', icon: 'ri-chat-3-line', count: '26' },
            { id: 'herramientas' as MobilePanel, label: 'Tools', icon: 'ri-tools-line', count: '' },
          ].map((p) => (
            <button
              key={p.id}
              onClick={() => setMobilePanel(p.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all ${
                mobilePanel === p.id
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-100 text-foreground-500'
              }`}
            >
              <i className={`${p.icon} text-[10px]`}></i>
              {p.label}
              {p.count && (
                <span className={`px-1.5 py-0 rounded-full text-[8px] font-bold ${
                  mobilePanel === p.id ? 'bg-background-50/30 text-background-50' : 'bg-background-200 text-foreground-500'
                }`}>{p.count}</span>
              )}
            </button>
          ))}
        </div>
      </header>

      {/* Main layout */}
      <div className="flex">

        <main className="flex-1 min-w-0 flex flex-col">
          {pageState === 'loading' && (
            <div className="flex-1 flex items-start justify-center py-10">
              <LoadingSkeleton variant="list" rows={10} className="w-full max-w-3xl px-4" />
            </div>
          )}
          {pageState === 'error' && (
            <div className="flex-1 flex items-center justify-center">
              <ErrorState
                title="Error al cargar el Studio"
                message="No pudimos cargar los datos del centro de operación. Intentá de nuevo."
                onRetry={loadData}
              />
            </div>
          )}
          {pageState === 'ready' && (
            <>
              {/* DESKTOP: 3-panel layout */}
              <div className="hidden lg:flex flex-1 min-h-0 h-[calc(100vh-64px)]">
                {/* Left: Oyentes */}
                <div className="w-[260px] border-r border-background-200 shrink-0 h-full">
                  <ListenersPanel />
                </div>
                {/* Center: Conversación */}
                <div className="flex-1 min-w-0 h-full">
                  <ConversationPanel />
                </div>
                {/* Right: Herramientas */}
                <div className="w-[320px] border-l border-background-200 shrink-0 h-full">
                  <ToolsPanel />
                </div>
              </div>

              {/* MOBILE: single panel */}
              <div className="lg:hidden flex-1 min-h-0 h-[calc(100vh-110px)]">
                {mobilePanel === 'oyentes' && <ListenersPanel />}
                {mobilePanel === 'conversacion' && <ConversationPanel />}
                {mobilePanel === 'herramientas' && <ToolsPanel />}
              </div>
            </>
          )}
        </main>
      </div>


    </div>
  );
}